import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, MessageSquareText, Loader2 } from 'lucide-react';
import { crmNotesAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { NoteCard } from './NoteCard';
import { AddNoteDialog } from './AddNoteDialog';
import { CreateFollowupDialog } from './CreateFollowupDialog';
import { toast } from 'sonner';

/**
 * CRMNotesPanel — feed + composer for CRM Profile notes (Agent + Agency).
 *
 * Placement: top-right of AgentProfilePage / AgencyProfilePage.
 */
export function CRMNotesPanel({ subjectType, subjectId, subjectAgencyId = null, canCreateNote = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [followupSource, setFollowupSource] = useState(null);

  const load = useCallback(async () => {
    if (!subjectType || !subjectId) return;
    setLoading(true);
    try {
      const { data } = await crmNotesAPI.list({ subject_type: subjectType, subject_id: subjectId });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load notes');
      setItems([]);
    } finally { setLoading(false); }
  }, [subjectType, subjectId]);

  useEffect(() => { load(); }, [load]);

  const openFollowupFromNote = (note) => {
    setFollowupSource({
      source_type: 'crm_note',
      source_id: note.note_id,
      subject_type: subjectType,
      subject_id: subjectId,
    });
    setShowFollowup(true);
  };

  return (
    <Card data-testid="crm-notes-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquareText size={16} className="text-slate-500" />
          CRM Notes
        </CardTitle>
        {canCreateNote && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="crm-notes-add-btn">
            <Plus size={14} className="mr-1" /> Add Note
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {loading ? (
          <div className="py-8 text-center text-slate-400">
            <Loader2 className="mx-auto animate-spin" size={20} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6" data-testid="crm-notes-empty">
            No notes yet. {canCreateNote ? 'Click Add Note to record a call, meeting, or email.' : ''}
          </div>
        ) : (
          <div className="space-y-3" data-testid="crm-notes-list">
            {items.map((n) => (
              <NoteCard
                key={n.note_id}
                note={n}
                currentUser={user}
                onCreateFollowup={openFollowupFromNote}
                onDeleted={load}
              />
            ))}
          </div>
        )}
      </CardContent>

      <AddNoteDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        subjectType={subjectType}
        subjectId={subjectId}
        onCreated={load}
      />

      {followupSource && (
        <CreateFollowupDialog
          open={showFollowup}
          onOpenChange={setShowFollowup}
          source={followupSource}
          defaultAssigneeAgencyId={subjectAgencyId}
          onCreated={() => setFollowupSource(null)}
        />
      )}
    </Card>
  );
}

export default CRMNotesPanel;
